import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import { detectPlatformName } from "@/lib/platform/detect";
import { LOCALE_STORAGE_KEY, resolveDeviceLocale } from "./detector";
import { GENERATED_NAMESPACES, resources } from "./resources.generated";

/** Declared by hand. Every locale the app can render — not what the
 * switchers offer: that is the `language_switcher_locales` dial
 * (`i18n/switcher.ts`, DUE-301), which hides RU while RU stays supported
 * here. `resourcesGenerated.test.ts` asserts it matches the locale
 * directories on disk, so a new `locales/<lng>/` without a line here — or a
 * line here without the files — fails CI. */
export const SUPPORTED_LOCALES = ["ru", "es", "en", "pt"] as const;
export type SupportedLocale = (typeof SUPPORTED_LOCALES)[number];

/** Namespaces are a fact about the directory, not a product decision, so they
 * come from the generated map: adding `locales/<lng>/<ns>.json` in all four
 * locales and running `npm --workspace web run i18n:generate` registers the
 * namespace with no edit here.
 *
 * `resources.generated.ts` holds one static import per locale × namespace.
 * That used to be an `import.meta.glob`, which auto-registered every file —
 * convenient, and silently unusable outside Vite: any other bundler leaves
 * the call in place and it throws `import_meta.glob is not a function` at
 * module load. Since almost everything reaches this module transitively,
 * that took the whole app down under esbuild (DUE-260's design-system
 * bundle) with an error that named nothing useful. Static imports work in
 * every bundler; the generator is what keeps writing them from being a
 * chore, and its `--check` mode is a test. */
export const I18N_NAMESPACES = GENERATED_NAMESPACES;

/** DUE-257: `<html lang>` is the only handle CSS has on the active locale,
 * and `tokens.css` swaps `--font-display` on it (Fraunces ships no Cyrillic).
 * The sync lives here rather than in App.tsx because i18next is what every
 * language transition goes through — boot, the session-locale swap, the
 * onboarding picker and the /profile switcher, on both the app and admin
 * entrypoints — so one subscription covers all of them. */
function syncDocumentLang(lng: string): void {
  document.documentElement.lang = lng;
}

function bootLocale(): string {
  // Only iOS auto-detects from the device: it has no Telegram initData and
  // no server-derived locale on the very first frame. Telegram/web boot in
  // English and App.tsx swaps to user.locale once the session lands
  // (MAV-114 — keeps a Spanish user on a Russian Telegram UI from flashing).
  if (detectPlatformName() === "ios") {
    const langs = navigator.languages ?? [navigator.language];
    return resolveDeviceLocale(langs, SUPPORTED_LOCALES);
  }
  return "en";
}

/** Boot i18next with the platform-appropriate initial locale (see
 * `bootLocale`) and let the App `useEffect` switch to the server-stored
 * locale once `/api/auth/session` resolves.
 *
 * MAV-114: we explicitly do NOT consult `Telegram.WebApp.initDataUnsafe.user.language_code`
 * any more on Telegram/web. The user's locale is a DB column
 * (`users.locale`) populated by the wizard / `/profile` switcher. Reading
 * it from Telegram on every refresh used to silently revert the user's
 * pick when their iOS Telegram UI was a different language. We boot in
 * English there to keep the first frame deterministic — the App-level
 * effect swaps to `user.locale` as soon as the session lands.
 */
export async function initI18n(): Promise<typeof i18n> {
  if (i18n.isInitialized) return i18n;
  await i18n.use(initReactI18next).init({
    resources,
    lng: bootLocale(),
    fallbackLng: "en",
    supportedLngs: SUPPORTED_LOCALES as unknown as string[],
    ns: I18N_NAMESPACES as unknown as string[],
    defaultNS: "common",
    interpolation: { escapeValue: false },
    returnNull: false,
  });
  syncDocumentLang(i18n.language);
  i18n.on("languageChanged", syncDocumentLang);
  return i18n;
}

export { i18n, LOCALE_STORAGE_KEY };
export default i18n;
